<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Penalty Management' : 'Deleted Penalties'"
              :subtitle="isDeletePage() != true ? 'Manage and track all employee penalties' : 'Restore or permanently delete penalties'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/penalties/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Penalty
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Penalties</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Input -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="searchKeyword"
                    placeholder="Search by remarks..."
                    solo
                    rounded
                    hide-details
                    class="search-field"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="grey" size="20">mdi-magnify</v-icon>
                    </template>
                  </v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="2">
                  <v-select
                    v-model="filters.status"
                    :items="statusOptions"
                    placeholder="Status"
                    solo
                    rounded
                    hide-details
                    clearable
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="grey" size="20">mdi-flag</v-icon>
                    </template>
                  </v-select>
                </v-col>

                <!-- User Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filters.user_id"
                    :items="users"
                    item-text="name"
                    item-value="id"
                    placeholder="Select User"
                    solo
                    rounded
                    hide-details
                    clearable
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="grey" size="20">mdi-account</v-icon>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Active Status Filter -->
                <v-col cols="12" md="2">
                  <v-select
                    v-model="filters.is_active"
                    :items="[
                      { text: 'Active', value: true },
                      { text: 'Inactive', value: false },
                    ]"
                    placeholder="Active Status"
                    solo
                    rounded
                    hide-details
                    clearable
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="grey" size="20">mdi-check-circle</v-icon>
                    </template>
                  </v-select>
                </v-col>

                <!-- Action Buttons -->
                <v-col cols="12" md="1">
                  <div class="d-flex">
                    <v-btn
                      color="success"
                      icon
                      small
                      @click="exportToExcel"
                      class="mr-1"
                    >
                      <v-icon size="20">mdi-microsoft-excel</v-icon>
                    </v-btn>
                    <v-btn
                      color="warning"
                      icon
                      small
                      @click="toggleDeletedView"
                    >
                      <v-icon size="20">{{
                        showDeleted ? "mdi-eye" : "mdi-delete"
                      }}</v-icon>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Section -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">
                      mdi-gavel
                    </v-icon>
                    <span class="text-body-2 font-weight-medium">
                      {{ showDeleted ? "Deleted" : "Active" }} Penalties
                      ({{ totalPenalties }})
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex justify-end align-center">
                    <v-chip
                      v-if="!isLoading"
                      color="info"
                      outlined
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-table</v-icon>
                      {{ penalties.length }} Rows
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider class="mx-4"></v-divider>

            <v-data-table
              :headers="headers"
              :items="penalties"
              :loading="isLoading"
              :server-items-length="totalPenalties"
              :options.sync="options"
              fixed-header
              height="285"
              class="elevation-0"
            >
              <!-- Custom Header Slot -->
              <template v-slot:header>
                <thead class="table-header">
                  <tr>
                    <th
                      v-for="header in headers"
                      :key="header.value"
                      :class="header.class"
                      class="text-caption font-weight-bold text-uppercase"
                    >
                      {{ header.text }}
                    </th>
                  </tr>
                </thead>
              </template>

              <!-- User Column -->
              <template v-slot:[`item.user`]="{ item }">
                <div class="d-flex align-center">
                  <v-avatar size="28" class="mr-2">
                    <v-icon size="16" color="primary">mdi-account</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-caption font-weight-medium">
                      {{ item.user ? item.user.name : "N/A" }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ item.user ? item.user.email : "" }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Status Column -->
              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  :color="getStatusColor(item.status)"
                  x-small
                  class="font-weight-medium"
                >
                  {{ getStatusText(item.status) }}
                </v-chip>
              </template>

              <!-- Penalty Type Column -->
              <template v-slot:[`item.penalty_type`]="{ item }">
                <v-chip
                  v-if="item.ban_from && item.ban_to"
                  color="error"
                  x-small
                  outlined
                >
                  <v-icon left x-small>mdi-gavel</v-icon>
                  Ban
                </v-chip>
                <v-chip
                  v-else-if="item.fine_amount"
                  color="warning"
                  x-small
                  outlined
                >
                  <v-icon left x-small>mdi-currency-inr</v-icon>
                  Fine
                </v-chip>
                <v-chip v-else color="grey" x-small outlined>
                  <v-icon left x-small>mdi-alert</v-icon>
                  Other
                </v-chip>
              </template>

              <!-- Fine Amount Column -->
              <template v-slot:[`item.fine_amount`]="{ item }">
                <span v-if="item.fine_amount" class="font-weight-medium">
                  ${{ parseFloat(item.fine_amount).toLocaleString() }}
                </span>
                <span v-else class="grey--text">N/A</span>
              </template>

              <!-- Ban Duration Column -->
              <template v-slot:[`item.ban_duration`]="{ item }">
                <div v-if="item.ban_from && item.ban_to">
                  <div class="text-caption">
                    {{ formatDate(item.ban_from) }}
                  </div>
                  <div class="text-caption grey--text">
                    to {{ formatDate(item.ban_to) }}
                  </div>
                </div>
                <span v-else class="grey--text">N/A</span>
              </template>

              <!-- Active Status Column -->
              <template v-slot:[`item.is_active`]="{ item }">
                <v-chip
                  :color="item.is_active ? 'success' : 'error'"
                  x-small
                  class="font-weight-medium"
                >
                  <v-icon left x-small>
                    {{ item.is_active ? "mdi-check" : "mdi-close" }}
                  </v-icon>
                  {{ item.is_active ? "Active" : "Inactive" }}
                </v-chip>
              </template>

              <!-- Actions Column -->
              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="primary"
                        @click="editPenalty(item)"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon size="16">mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>

                  <v-tooltip top v-if="!showDeleted">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="error"
                        @click="deletePenalty(item)"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon size="16">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>

                  <v-tooltip top v-if="showDeleted">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="success"
                        @click="restorePenalty(item)"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon size="16">mdi-restore</v-icon>
                      </v-btn>
                    </template>
                    <span>Restore</span>
                  </v-tooltip>
                </div>
              </template>

              <!-- Loading Slot -->
              <template v-slot:loading>
                <div class="text-center py-4">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                  ></v-progress-circular>
                  <p class="mt-2 text-caption grey--text">Loading penalties...</p>
                </div>
              </template>

              <!-- No Data Slot -->
              <template v-slot:no-data>
                <empty-state icon="mdi-gavel" title="No penalties found" />
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon color="error" class="mr-2">mdi-delete</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete this penalty? This action can be
          undone later.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            @click="confirmDelete"
            :loading="isDeleting"
            text
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Restore Confirmation Dialog -->
    <v-dialog v-model="restoreDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon color="success" class="mr-2">mdi-restore</v-icon>
          Confirm Restore
        </v-card-title>
        <v-card-text>
          Are you sure you want to restore this penalty?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="restoreDialog = false">Cancel</v-btn>
          <v-btn
            color="success"
            @click="confirmRestore"
            :loading="isRestoring"
            text
          >
            Restore
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "PenaltiesIndex",
  data() {
    return {
      penalties: [],
      users: [],
      totalPenalties: 0,
      isLoading: false,
      isDeleting: false,
      isRestoring: false,
      searchKeyword: "",
      showDeleted: false,
      deleteDialog: false,
      restoreDialog: false,
      selectedPenalty: null,
      
      filters: {
        status: null,
        user_id: null,
        is_active: null,
      },

      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },

      statusOptions: [
        { text: "Pending", value: 0 },
        { text: "In Process", value: 1 },
        { text: "Completed", value: 2 },
        { text: "Rejected", value: 3 },
      ],

      headers: [
        {
          text: "User",
          value: "user",
          sortable: false,
          width: "200px",
        },
        {
          text: "Status",
          value: "status",
          sortable: true,
          width: "120px",
        },
        {
          text: "Type",
          value: "penalty_type",
          sortable: false,
          width: "100px",
        },
        {
          text: "Fine Amount",
          value: "fine_amount",
          sortable: true,
          width: "120px",
        },
        {
          text: "Ban Duration",
          value: "ban_duration",
          sortable: false,
          width: "160px",
        },
        {
          text: "Remarks",
          value: "remark",
          sortable: false,
        },
        {
          text: "Active",
          value: "is_active",
          sortable: true,
          width: "100px",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "120px",
          align: "center",
        },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.searchKeyword ||
        this.filters.status !== null ||
        this.filters.user_id !== null ||
        this.filters.is_active !== null
      );
    },
  },

  watch: {
    searchKeyword() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.getPenalties();
      }, 500);
    },

    "filters.status"() {
      this.getPenalties();
    },

    "filters.user_id"() {
      this.getPenalties();
    },

    "filters.is_active"() {
      this.getPenalties();
    },

    options: {
      handler() {
        this.getPenalties();
      },
      deep: true,
    },

    showDeleted() {
      this.getPenalties();
    },
  },

  mounted() {
    this.checkDeletedRoute();
    this.getPenalties();
    this.getUsers();
  },

  methods: {
    async getPenalties() {
      this.isLoading = true;
      try {
        const params = {
          page: this.options.page,
          rowsPerPage: this.options.itemsPerPage,
          show_deleted: this.showDeleted ? 1 : 0,
        };

        if (this.searchKeyword) {
          params.search_keyword = this.searchKeyword;
        }

        if (this.filters.status !== null) {
          params.status = this.filters.status;
        }

        if (this.filters.user_id) {
          params.user_id = this.filters.user_id;
        }

        if (this.filters.is_active !== null) {
          params.is_active = this.filters.is_active ? 1 : 0;
        }

        const response = await axios.get("/penalties", { params });

        if (response.data.success) {
          this.penalties = response.data.data;
          this.totalPenalties = response.data.count;
        }
      } catch (error) {
        this.$store.dispatch("errors/setError", {
          message: "Failed to load penalties",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async getUsers() {
      try {
        const response = await axios.get("/penalties/users");
        if (response.data.success) {
          this.users = response.data.data;
        }
      } catch (error) {
        console.error("Failed to load users:", error);
      }
    },

    editPenalty(penalty) {
      this.$router.push(`/penalties/${penalty.id}`);
    },

    deletePenalty(penalty) {
      this.selectedPenalty = penalty;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.isDeleting = true;
      try {
        const response = await axios.post(
          `/penalties/delete/${this.selectedPenalty.id}`
        );

        if (response.data.success) {
          this.$store.dispatch("errors/setError", {
            message: "Penalty deleted successfully",
            type: "success",
          });
          this.getPenalties();
        }
      } catch (error) {
        this.$store.dispatch("errors/setError", {
          message: "Failed to delete penalty",
        });
      } finally {
        this.isDeleting = false;
        this.deleteDialog = false;
        this.selectedPenalty = null;
      }
    },

    restorePenalty(penalty) {
      this.selectedPenalty = penalty;
      this.restoreDialog = true;
    },

    async confirmRestore() {
      this.isRestoring = true;
      try {
        const response = await axios.post(
          `/penalties/restore/${this.selectedPenalty.id}`
        );

        if (response.data.success) {
          this.$store.dispatch("errors/setError", {
            message: "Penalty restored successfully",
            type: "success",
          });
          this.getPenalties();
        }
      } catch (error) {
        this.$store.dispatch("errors/setError", {
          message: "Failed to restore penalty",
        });
      } finally {
        this.isRestoring = false;
        this.restoreDialog = false;
        this.selectedPenalty = null;
      }
    },

    async exportToExcel() {
      try {
        const params = {
          show_deleted: this.showDeleted ? 1 : 0,
        };

        if (this.searchKeyword) {
          params.search_keyword = this.searchKeyword;
        }

        if (this.filters.status !== null) {
          params.status = this.filters.status;
        }

        if (this.filters.user_id) {
          params.user_id = this.filters.user_id;
        }

        if (this.filters.is_active !== null) {
          params.is_active = this.filters.is_active ? 1 : 0;
        }

        window.location.href = `/api/penalties/excel?${new URLSearchParams(
          params
        )}`;

        this.$store.dispatch("errors/setError", {
          message: "Export started successfully",
          type: "success",
        });
      } catch (error) {
        this.$store.dispatch("errors/setError", {
          message: "Failed to export penalties",
        });
      }
    },

    toggleDeletedView() {
      this.showDeleted = !this.showDeleted;
      this.$router.push({
        path: this.showDeleted ? "/penalties/deleted" : "/penalties",
      });
    },

    checkDeletedRoute() {
      this.showDeleted = this.$route.path.includes("/deleted");
    },

    isDeletePage() {
      return this.showDeleted;
    },

    clearAllFilters() {
      this.searchKeyword = "";
      this.filters = {
        status: null,
        user_id: null,
        is_active: null,
      };
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.searchKeyword) count++;
      if (this.filters.status !== null) count++;
      if (this.filters.user_id) count++;
      if (this.filters.is_active !== null) count++;
      return count;
    },

    getStatusText(status) {
      const statusMap = {
        0: "Pending",
        1: "In Process",
        2: "Completed",
        3: "Rejected",
      };
      return statusMap[status] || "Unknown";
    },

    getStatusColor(status) {
      const colorMap = {
        0: "warning",
        1: "info",
        2: "success",
        3: "error",
      };
      return colorMap[status] || "grey";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.search-field >>> .v-input__slot {
  background-color: #fafafa !important;
}

.table-header th {
  background-color: #f5f5f5 !important;
  color: #333 !important;
  font-weight: 600 !important;
}

.v-chip--x-small {
  height: 20px !important;
  font-size: 10px !important;
}

.v-data-table >>> .v-data-table__wrapper {
  border-radius: 0 0 16px 16px;
}
</style>